import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/apiService';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        onClose();
      } else if (mode === 'register') {
        const data = await authApi.register(email, password, displayName);
        setMessage(data.message);
        setMode('login');
      } else {
        const data = await authApi.forgotPassword(email);
        setMessage(data.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = authApi.googleAuthUrl();
  };

  const switchMode = (newMode: 'login' | 'register' | 'forgot') => {
    setMode(newMode);
    setError('');
    setMessage('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setConfirmPassword('');
  };

  const getTitle = () => {
    if (mode === 'login') return 'Sign In';
    if (mode === 'register') return 'Create Account';
    return 'Reset Password';
  };

  const getSubmitLabel = () => {
    if (mode === 'login') return 'Sign In';
    if (mode === 'register') return 'Create Account';
    return 'Send Reset Link';
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={closeButtonStyle}>&times;</button>

        <div style={tabsStyle}>
          <button
            onClick={() => switchMode('login')}
            style={mode === 'login' ? activeTabStyle : tabStyle}
          >
            Sign In
          </button>
          <button
            onClick={() => switchMode('register')}
            style={mode === 'register' ? activeTabStyle : tabStyle}
          >
            Sign Up
          </button>
        </div>

        <h2 style={titleStyle}>{getTitle()}</h2>

        {error && <p style={errorStyle}>{error}</p>}
        {message && <p style={successStyle}>{message}</p>}

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={inputStyle}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
          />
          {mode !== 'forgot' && (
            <>
              <div style={passwordWrapperStyle}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  style={passwordInputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={eyeButtonStyle}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {mode === 'register' && (
                <div style={passwordWrapperStyle}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    style={passwordInputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={eyeButtonStyle}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              )}
            </>
          )}
          <button type="submit" disabled={isLoading} style={submitButtonStyle}>
            {isLoading ? '...' : getSubmitLabel()}
          </button>
        </form>

        {mode === 'login' && (
          <p style={forgotStyle}>
            <span onClick={() => switchMode('forgot')} style={forgotLinkStyle}>
              Forgot password?
            </span>
          </p>
        )}

        {mode === 'forgot' && (
          <p style={forgotStyle}>
            <span onClick={() => switchMode('login')} style={forgotLinkStyle}>
              Back to sign in
            </span>
          </p>
        )}

        {mode !== 'forgot' && (
          <>
            <div style={dividerStyle}>
              <span style={dividerTextStyle}>or</span>
            </div>

            <button onClick={handleGoogleLogin} style={googleButtonStyle}>
              Sign in with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.7)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: '#2e2020', borderRadius: '16px', padding: '2rem',
  width: '90%', maxWidth: '400px', position: 'relative', color: '#b08a8a',
};

const closeButtonStyle: React.CSSProperties = {
  position: 'absolute', top: '12px', right: '16px', background: 'none',
  border: 'none', color: '#b08a8a', fontSize: '1.5rem', cursor: 'pointer',
};

const tabsStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '1rem',
};

const tabStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #815353', borderRadius: '20px',
  padding: '6px 18px', cursor: 'pointer', color: '#815353', fontSize: '0.9rem',
};

const activeTabStyle: React.CSSProperties = {
  ...tabStyle,
  background: '#815353', color: '#fff', borderColor: '#815353',
};

const titleStyle: React.CSSProperties = {
  color: '#fff', textAlign: 'center', marginBottom: '1.5rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: '25px', border: 'none',
  background: '#3d2a2a', color: '#fff', fontSize: '1rem', marginBottom: '0.75rem',
  boxSizing: 'border-box',
};

const passwordWrapperStyle: React.CSSProperties = {
  position: 'relative', marginBottom: '0.75rem',
};

const passwordInputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 44px 12px 16px', borderRadius: '25px', border: 'none',
  background: '#3d2a2a', color: '#fff', fontSize: '1rem',
  boxSizing: 'border-box',
};

const eyeButtonStyle: React.CSSProperties = {
  position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  fontSize: '0.95rem', padding: '4px', display: 'flex', alignItems: 'center',
};

const submitButtonStyle: React.CSSProperties = {
  width: '100%', padding: '12px', borderRadius: '25px', border: 'none',
  background: '#815353', color: '#fff', fontWeight: 'bold', fontSize: '1rem',
  cursor: 'pointer', marginTop: '0.5rem',
};

const forgotStyle: React.CSSProperties = {
  textAlign: 'center', marginTop: '0.75rem', fontSize: '0.85rem',
};

const forgotLinkStyle: React.CSSProperties = {
  color: '#b08a8a', cursor: 'pointer', textDecoration: 'underline',
};

const dividerStyle: React.CSSProperties = {
  textAlign: 'center', margin: '1.25rem 0', position: 'relative',
  borderTop: '1px solid #815353',
};

const dividerTextStyle: React.CSSProperties = {
  background: '#2e2020', padding: '0 12px', position: 'relative', top: '-10px',
  color: '#815353',
};

const googleButtonStyle: React.CSSProperties = {
  width: '100%', padding: '12px', borderRadius: '25px',
  border: '1px solid #b08a8a', background: 'transparent',
  color: '#b08a8a', fontSize: '1rem', cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  color: '#ff6b6b', textAlign: 'center', marginBottom: '1rem',
};

const successStyle: React.CSSProperties = {
  color: '#b08a8a', textAlign: 'center', marginBottom: '1rem',
  background: 'rgba(129, 83, 83, 0.15)', borderRadius: '8px', padding: '8px',
};

export default AuthModal;
